<template>
  <section
    class="tcal"
    :class="{ 'tcal--narrow': narrow, 'tcal--by-teammate': colorBy === 'teammate' }"
    data-test="tasks-calendar"
    aria-label="Tasks calendar"
  >
    <header class="tcal__bar">
      <div class="tcal__nav">
        <button type="button" class="tcal__icon" aria-label="Previous" data-test="tasks-calendar-prev" @click="shift(-1)">
          <svg viewBox="0 0 24 24" width="16" height="16" class="tcal__svg" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <button type="button" class="tcal__icon" aria-label="Next" data-test="tasks-calendar-next" @click="shift(1)">
          <svg viewBox="0 0 24 24" width="16" height="16" class="tcal__svg" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6" /></svg>
        </button>
        <button type="button" class="tcal__today" data-test="tasks-calendar-today" @click="goToday">Today</button>
        <h2 class="tcal__label" data-test="tasks-calendar-label" aria-live="polite">{{ label }}</h2>
      </div>
      <div class="tcal__tools">
        <label class="tcal__check">
          <input type="checkbox" :checked="showDone" data-test="tasks-calendar-done" @change="$emit('update:show-done', $event.target.checked)" />
          <span>Completed</span>
        </label>
        <div class="tcal__modes" role="group" aria-label="Colour chips by">
          <button
            v-for="c in colorChoices"
            :key="c.key"
            type="button"
            class="tcal__mode"
            :class="{ 'is-on': colorBy === c.key }"
            :aria-pressed="colorBy === c.key ? 'true' : 'false'"
            :data-test="`tasks-calendar-color-${c.key}`"
            @click="colorBy = c.key"
          >{{ c.label }}</button>
        </div>
        <div class="tcal__modes" role="group" aria-label="Calendar range">
          <button
            v-for="m in modes"
            :key="m.key"
            type="button"
            class="tcal__mode"
            :class="{ 'is-on': mode === m.key }"
            :aria-pressed="mode === m.key ? 'true' : 'false'"
            :data-test="`tasks-calendar-mode-${m.key}`"
            @click="setMode(m.key)"
          >{{ m.label }}</button>
        </div>
      </div>
    </header>

    <ul v-if="colorBy === 'teammate' && (legend.people.length || legend.none)" class="tcal__legend" data-test="tasks-calendar-legend">
      <li v-for="p in legend.people" :key="p.id" class="tcal__legend-item">
        <span class="tcal__dot" :class="`tcal__dot--t${colorOf(p.id)}`" aria-hidden="true"></span>{{ p.name }}
      </li>
      <li v-if="legend.none" class="tcal__legend-item"><span class="tcal__dot tcal__dot--none" aria-hidden="true"></span>Unassigned</li>
    </ul>

    <div v-if="!narrow || mode === 'month'" class="tcal__dows" aria-hidden="true">
      <span v-for="d in dowLabels" :key="d" class="tcal__dow">{{ d }}</span>
    </div>

    <div class="tcal__grid" :class="`tcal__grid--${mode}`" data-test="tasks-calendar-grid">
      <template v-for="(week, wi) in rows">
        <div
          v-for="cell in week"
          :key="`${wi}-${cell.ymd}`"
          class="tcal__day"
          :class="{
            'tcal__day--out': !cell.inMonth,
            'tcal__day--today': cell.ymd === ctx.today,
            'tcal__day--over': dropYmd === cell.ymd,
          }"
          :data-ymd="cell.ymd"
          data-test="tasks-calendar-day"
          @dragover.prevent="onDragOver(cell.ymd)"
          @dragleave="onDragLeave(cell.ymd)"
          @drop.prevent="onDrop(cell.ymd)"
        >
          <div class="tcal__head">
            <button
              type="button"
              class="tcal__num"
              :aria-label="`${cell.ymd}${dayList(cell.ymd).length ? `, ${dayList(cell.ymd).length} tasks` : ''}`"
              @click="onDayClick(cell.ymd)"
            >{{ narrow && mode === 'week' ? `${weekdayName(cell.ymd)} ${dayNumber(cell)}` : dayNumber(cell) }}</button>
            <button
              v-if="!(narrow && mode === 'month')"
              type="button"
              class="tcal__add"
              :aria-label="`Add a task due ${cell.ymd}`"
              title="Add a task for this day"
              data-test="tasks-calendar-add"
              @click="$emit('add', cell.ymd)"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" class="tcal__svg" aria-hidden="true" focusable="false"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
            </button>
          </div>

          <template v-if="narrow && mode === 'month'">
            <span v-if="dayList(cell.ymd).length" class="tcal__count" data-test="tasks-calendar-count">{{ dayList(cell.ymd).length }}</span>
          </template>
          <template v-else>
            <ul class="tcal__list">
              <li v-for="r in visible(cell.ymd).shown" :key="r.task.id">
                <button
                  type="button"
                  class="tcal__chip"
                  :class="chipClass(r)"
                  :draggable="canDrag(r) ? 'true' : 'false'"
                  :title="chipTitle(r)"
                  data-test="tasks-calendar-chip"
                  :data-task-id="r.task.id"
                  @click="$emit('open', r.task)"
                  @dragstart="onDragStart($event, r)"
                  @dragend="onDragEnd"
                >
                  <span class="tcal__chip-text">{{ r.meta.text || 'Untitled task' }}</span>
                  <span v-if="initialsOf(r)" class="tcal__chip-who">{{ initialsOf(r) }}</span>
                </button>
              </li>
            </ul>
            <button
              v-if="visible(cell.ymd).more"
              type="button"
              class="tcal__more"
              data-test="tasks-calendar-more"
              @click="expand(cell.ymd)"
            >+{{ visible(cell.ymd).more }} more</button>
            <p v-if="narrow && mode === 'week' && !dayList(cell.ymd).length" class="tcal__none">No tasks</p>
          </template>
        </div>
      </template>
    </div>

    <p v-if="undatedCount" class="tcal__note" data-test="tasks-calendar-undated">
      {{ undatedCount }} {{ undatedCount === 1 ? 'task has' : 'tasks have' }} no due date, so {{ undatedCount === 1 ? 'it is' : 'they are' }} not on the calendar.
    </p>
    <p v-if="narrow" class="tcal__hint" data-test="tasks-calendar-hint">
      Tap a task to open it and change its due date.
    </p>
    <template v-else>
      <p class="tcal__hint" data-test="tasks-calendar-hint">
        Drag a task onto another day to change its due date. Repeating and completed tasks stay where they are.
      </p>
      <p class="tcal__hint" data-test="tasks-calendar-keys">
        Keys: <kbd>T</kbd> today · <kbd>←</kbd> <kbd>→</kbd> previous / next · <kbd>M</kbd> month · <kbd>W</kbd> week
      </p>
    </template>
  </section>
</template>

<script>
/**
 * TasksCalendar — month / week calendar of the Tasks page (Super Admin preview, 2026-10-16).
 *
 *   <tasks-calendar :tasks="…" :ctx="ctx" :employees="employees" :narrow="tkNarrow" :show-done="…"
 *                   @open="tkOpen" @reschedule="({ task, ymd }) => …" @add="ymd => …"
 *                   @update:show-done="…" />
 *
 * Each task sits on its due day (the same day the list rows show). A month cell lists 3, then "+N
 * more". A chip drags onto another day and emits `reschedule`; the PAGE saves it (rescheduleToDayPatch
 * → tasks/updateTask), so this component never writes. Repeating and completed tasks are not draggable
 * (moving a repeating one re-anchors its whole series — the same rule as "Move all to today"). Click a
 * chip → `open` (the task drawer). The "+" on a day → `add(ymd)` (the page opens its composer with that
 * due day). "Completed" shows completed tasks too (struck through). Chips are coloured by status
 * (overdue red) or by teammate (first assignee) with a legend. Keys T / ← / → / M / W (calendarView.js
 * calendarKeyAction) work anywhere on the page except inside a text box, a dialog or the task drawer.
 *
 * Only tasks on the DAYS ON SCREEN are parsed for their title (rowMeta); the rest cost one date read each.
 * Narrow (phone): the month shows one count per day (tap a day → that week), the week is a stacked
 * list. Dragging is mouse-only (HTML5 drag-and-drop does not fire on touch): on a phone, open the task
 * and change its due date in the drawer. All grid maths is in src/utils/tasks/calendarView.js.
 *
 * Never reads $route / $store. No prop named patNum / siteID / loading (the mixin would shadow it).
 */
import { rowMeta, dueDayOf, assigneeIDs, isRepeating } from '@/utils/tasks/myDay.js';
import {
  monthGrid, weekDays, shiftAnchor, rangeLabel, bucketByDay, splitDay, weekdayIndex,
  calendarKeyAction, teammateColorMap, legendOf,
} from '@/utils/tasks/calendarView.js';

const WEEK_START = 0; // Sunday, as the rest of the app's calendars
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function nameInitials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

export default {
  name: 'TasksCalendar',
  props: {
    tasks: { type: Array, default: () => [] },
    // myDay.js ctx: { userID, today: 'YYYY-MM-DD', timeZone, clean? }.
    ctx: { type: Object, required: true },
    // state.customer.employees (EmployeeID, Name) — for the initials on a chip and the legend.
    employees: { type: Array, default: () => [] },
    narrow: { type: Boolean, default: false },
    // The page owns "show completed" (it decides which lists feed `tasks`).
    showDone: { type: Boolean, default: false },
  },

  data() {
    return {
      mode: 'month',
      anchor: this.ctx.today,
      expanded: {},
      dragId: null,
      dropYmd: null,
      colorBy: 'status',
      modes: [{ key: 'month', label: 'Month' }, { key: 'week', label: 'Week' }],
      colorChoices: [{ key: 'status', label: 'Status' }, { key: 'teammate', label: 'Teammate' }],
    };
  },

  computed: {
    dowLabels() {
      const out = [];
      for (let i = 0; i < 7; i += 1) out.push(DOW[(WEEK_START + i) % 7]);
      return out;
    },
    label() {
      return rangeLabel(this.anchor, this.mode, WEEK_START);
    },
    rows() {
      if (this.mode === 'week') return [weekDays(this.anchor, WEEK_START).map(ymd => ({ ymd, inMonth: true }))];
      return monthGrid(this.anchor, WEEK_START).weeks;
    },
    daysOnScreen() {
      const set = {};
      this.rows.forEach(w => w.forEach((c) => { set[c.ymd] = true; }));
      return set;
    },
    // One cheap date read per task; the title is parsed only for tasks on a day that is on screen.
    dated() {
      return (this.tasks || [])
        .filter(t => t && t.id !== undefined && t.id !== null)
        .map(task => ({ task, dueDay: dueDayOf(task, this.ctx) }));
    },
    entries() {
      return this.dated
        .filter(e => e.dueDay && this.daysOnScreen[e.dueDay])
        .map((e) => {
          const meta = rowMeta(e.task, this.ctx);
          return { task: e.task, meta, dueDay: e.dueDay, assigneeEmpIDs: meta.assigneeEmpIDs };
        });
    },
    buckets() {
      return bucketByDay(this.entries);
    },
    undatedCount() {
      return this.dated.filter(e => !e.dueDay).length;
    },
    nameById() {
      const m = {};
      (this.employees || []).forEach((e) => { if (e && e.EmployeeID !== undefined) m[String(e.EmployeeID)] = e.Name; });
      return m;
    },
    // Every id that can be coloured, from the roster AND the tasks on screen, so a colour never moves
    // when a month changes.
    colorMap() {
      const ids = (this.employees || []).map(e => e && e.EmployeeID);
      (this.tasks || []).forEach((t) => { assigneeIDs(t).forEach(id => ids.push(id)); });
      return teammateColorMap(ids);
    },
    legend() {
      return legendOf(this.entries, this.nameById);
    },
  },

  watch: {
    // The page's "today" ticks over at midnight; a calendar left on the old today follows only when it
    // was showing it.
    'ctx.today'(now, was) {
      if (this.anchor === was) this.anchor = now;
    },
  },

  mounted() {
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.onKey);
  },

  beforeDestroy() {
    if (typeof window !== 'undefined') window.removeEventListener('keydown', this.onKey);
  },

  methods: {
    dayList(ymd) {
      return this.buckets[ymd] || [];
    },
    visible(ymd) {
      return splitDay(this.dayList(ymd), !!this.expanded[ymd] || this.mode === 'week');
    },
    expand(ymd) {
      this.expanded = Object.assign({}, this.expanded, { [ymd]: true });
    },
    dayNumber(cell) {
      return Number(cell.ymd.slice(8, 10));
    },
    weekdayName(ymd) {
      return DOW[weekdayIndex(ymd)];
    },
    setMode(key) {
      this.mode = key;
      this.expanded = {};
    },
    shift(dir) {
      this.anchor = shiftAnchor(this.anchor, this.mode, dir);
      this.expanded = {};
    },
    goToday() {
      this.anchor = this.ctx.today;
      this.expanded = {};
    },
    /** T / ← / → / M / W. Never while typing, with a modifier, or while a dialog or the task drawer is in front. */
    onKey(e) {
      if (!e || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      if (t && typeof t.closest === 'function' && t.closest('.slideout-panel, [role="dialog"], .modal')) return;
      const action = calendarKeyAction(e.key);
      if (!action) return;
      e.preventDefault();
      if (action === 'today') this.goToday();
      else if (action === 'prev') this.shift(-1);
      else if (action === 'next') this.shift(1);
      else this.setMode(action);
    },
    onDayClick(ymd) {
      // Phone month: a tap on a day opens that week; otherwise the number is just a label.
      if (this.narrow && this.mode === 'month') {
        this.anchor = ymd;
        this.mode = 'week';
        this.expanded = {};
      }
    },
    colorOf(id) {
      const n = this.colorMap[String(id)];
      return n === undefined ? 0 : n;
    },
    initialsOf(r) {
      const id = (r.meta.assigneeEmpIDs || [])[0];
      if (id === undefined || id === null) return '';
      return nameInitials(this.nameById[String(id)]) || '?';
    },
    canDrag(r) {
      return r.task.status === 'INCOMPLETE' && !isRepeating(r.task);
    },
    chipClass(r) {
      const done = r.task.status === 'COMPLETED';
      const out = {
        'tcal__chip--done': done,
        'tcal__chip--late': !done && !!r.dueDay && r.dueDay < this.ctx.today,
        'tcal__chip--fixed': !this.canDrag(r),
        'tcal__chip--drag': String(this.dragId) === String(r.task.id),
      };
      const first = (r.meta.assigneeEmpIDs || [])[0];
      if (this.colorBy === 'teammate' && first !== undefined && first !== null) out[`tcal__chip--t${this.colorOf(first)}`] = true;
      return out;
    },
    chipTitle(r) {
      const t = r.meta.text || 'Untitled task';
      if (r.task.status === 'COMPLETED') return `${t} (completed)`;
      if (isRepeating(r.task)) return `${t} — repeating tasks cannot be moved from the calendar`;
      return t;
    },
    onDragStart(e, r) {
      if (!this.canDrag(r)) {
        e.preventDefault();
        return;
      }
      this.dragId = r.task.id;
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(r.task.id)); } catch (err) { /* IE/Edge legacy */ }
      }
    },
    onDragEnd() {
      this.dragId = null;
      this.dropYmd = null;
    },
    onDragOver(ymd) {
      if (this.dragId !== null) this.dropYmd = ymd;
    },
    onDragLeave(ymd) {
      if (this.dropYmd === ymd) this.dropYmd = null;
    },
    onDrop(ymd) {
      const id = this.dragId;
      this.dragId = null;
      this.dropYmd = null;
      if (id === null) return;
      const hit = this.entries.find(e => String(e.task.id) === String(id));
      if (hit && hit.dueDay !== ymd) this.$emit('reschedule', { task: hit.task, ymd });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

// One colour per teammate slot (calendarView.js TEAMMATE_COLORS = 8). The theme has only five hues that are
// neither red (the overdue colour) nor a second green, so these three are local, chart-only hues.
$tcal-cyan: #0891b2;
$tcal-slate: #64748b;
$tcal-fuchsia: #c026d3;
$tcal-colors: ($fu-brand, $fu-info, $fu-cat-claim, $fu-warning, $tcal-cyan, $tcal-slate, $fu-orange, $tcal-fuchsia);

.tcal {
  min-width: 0;
  margin-top: $fu-space-4;
}

.tcal__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: $fu-space-3;
  margin-bottom: $fu-space-3;
}

.tcal__nav {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  min-width: 0;
}

.tcal__tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $fu-space-3;
}

.tcal__label {
  margin: 0 0 0 $fu-space-2;
  font-size: 18px;
  font-weight: $fu-fw-bold;
  color: $fu-text-strong;
}

.tcal__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tcal__icon {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: $fu-radius-sm;
  border: 1px solid $fu-border;
  background: $fu-surface;
  color: $fu-neutral-600;
  &:hover { background: $fu-neutral-50; color: $fu-text; }
}

.tcal__today {
  @include fu-chip;
  height: 32px;
  padding: 0 12px;
}

.tcal__check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  cursor: pointer;
  input { margin: 0; }
}

.tcal__modes {
  display: inline-flex;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-sm + 2px;
  overflow: hidden;
  background: $fu-surface;
}

.tcal__mode {
  @include fu-button-reset;
  @include fu-focus-ring;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  &:hover { background: $fu-neutral-50; }
  &.is-on { background: $fu-brand; color: #ffffff; }
}

.tcal__legend {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0 0 $fu-space-3;
  padding: 0;
}

.tcal__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $fu-neutral-600;
}

.tcal__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
  background: $fu-neutral-300;
}

@each $c in $tcal-colors {
  $i: index($tcal-colors, $c) - 1;
  .tcal__dot--t#{$i} { background: $c; }
}

.tcal__dows {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid $fu-border;
  border-bottom: 0;
  border-radius: $fu-radius-md $fu-radius-md 0 0;
  background: $fu-neutral-50;
}

.tcal__dow {
  padding: 7px 8px;
  font-size: 11.5px;
  font-weight: $fu-fw-bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $fu-text-muted;
}

.tcal__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border-top: 1px solid $fu-border;
  border-left: 1px solid $fu-border;
  background: $fu-surface;
  border-radius: 0 0 $fu-radius-md $fu-radius-md;
  overflow: hidden;
}

.tcal__grid--week .tcal__day { min-height: 240px; }
// A week has the room: show the whole title, not the first few letters.
.tcal__grid--week .tcal__chip-text { white-space: normal; overflow-wrap: anywhere; }
.tcal__grid--week .tcal__chip { align-items: flex-start; }

.tcal__day {
  min-width: 0;
  min-height: 112px;
  padding: 6px;
  border-right: 1px solid $fu-border;
  border-bottom: 1px solid $fu-border;
  transition: background $fu-dur-fast $fu-ease-out;
}

.tcal__day--out { background: $fu-neutral-25; }
.tcal__day--out .tcal__num { color: $fu-text-subtle; }
.tcal__day--over { background: $fu-brand-50; box-shadow: inset 0 0 0 2px $fu-brand-300; }
.tcal__day--today .tcal__num { background: $fu-brand; color: #ffffff; }

.tcal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tcal__num {
  @include fu-button-reset;
  @include fu-focus-ring;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: $fu-radius-pill;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  text-align: center;
  @include fu-tabular;
}

// The "+" shows on hover / keyboard focus on a desktop, and always on a phone (no hover there).
.tcal__add {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: $fu-radius-xs;
  color: $fu-neutral-500;
  opacity: 0;
  transition: opacity $fu-dur-fast $fu-ease-out;
  &:hover { background: $fu-brand-50; color: $fu-brand-700; }
  &:focus { opacity: 1; }
}

.tcal__day:hover .tcal__add,
.tcal__day:focus-within .tcal__add { opacity: 1; }

.tcal__list {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.tcal__chip {
  @include fu-button-reset;
  @include fu-focus-ring;
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  border-radius: $fu-radius-xs + 1px;
  background: $fu-brand-50;
  border-left: 3px solid $fu-brand;
  font-size: 12px;
  line-height: 1.3;
  color: $fu-text;
  text-align: left;
  cursor: grab;
  &:hover { background: $fu-brand-100; }
  &:active { cursor: grabbing; }
}

.tcal__chip--late { background: $fu-danger-subtle; border-left-color: $fu-danger; color: $fu-danger-text; &:hover { background: $fu-danger-subtle; } }
.tcal__chip--done { background: $fu-neutral-50; border-left-color: $fu-neutral-300; color: $fu-text-subtle; text-decoration: line-through; }
.tcal__chip--fixed { cursor: pointer; }
.tcal__chip--drag { opacity: 0.45; }

// By teammate: every chip is neutral with the teammate's colour on its edge; overdue is a red dot + red text.
.tcal--by-teammate {
  .tcal__chip { background: $fu-neutral-50; &:hover { background: $fu-neutral-100; } }
  .tcal__chip--late { color: $fu-danger-text; &::after { content: ''; flex: none; width: 6px; height: 6px; border-radius: 50%; background: $fu-danger; } }
  .tcal__chip--done { color: $fu-text-subtle; border-left-color: $fu-neutral-300; }
  @each $c in $tcal-colors {
    $i: index($tcal-colors, $c) - 1;
    .tcal__chip--t#{$i}:not(.tcal__chip--done) { border-left-color: $c; }
  }
  .tcal__chip:not([class*='tcal__chip--t']):not(.tcal__chip--done) { border-left-color: $fu-neutral-300; }
}

.tcal__chip-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tcal__chip-who {
  flex: none;
  font-size: 9px;
  font-weight: $fu-fw-bold;
  color: $fu-neutral-600;
}

.tcal__more {
  @include fu-button-reset;
  @include fu-focus-ring;
  margin-top: 3px;
  padding: 1px 4px;
  font-size: 11.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
  &:hover { text-decoration: underline; }
}

.tcal__count {
  @include fu-pill($fu-brand-50, $fu-brand-700);
  margin-left: 4px;
}

.tcal__none {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: $fu-text-subtle;
}

.tcal__note,
.tcal__hint {
  margin: $fu-space-3 0 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.tcal__hint + .tcal__hint { margin-top: 4px; }

.tcal__hint kbd {
  padding: 0 5px;
  border: 1px solid $fu-border-strong;
  border-radius: $fu-radius-xs;
  background: $fu-neutral-50;
  font-size: 11.5px;
  color: $fu-neutral-600;
}

// Phone: the month is a grid of numbers + counts; the week is seven stacked days.
.tcal--narrow {
  .tcal__day { min-height: 56px; padding: 4px; }
  .tcal__grid--week { grid-template-columns: minmax(0, 1fr); border-radius: $fu-radius-md; }
  .tcal__grid--week .tcal__day { min-height: 0; padding: 8px 10px; }
  .tcal__dow { padding: 6px 2px; text-align: center; font-size: 10.5px; }
  .tcal__label { font-size: 16px; }
  .tcal__add { opacity: 1; }
}
</style>
